import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { Search } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress, ProgressLabel, ProgressValue } from '@/components/ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { computeFocTourCoverage } from 'components/MapPage/focRequirementsTracker/focRequirementsCatalog';

const VERIFIED_UX_STORAGE_KEY = 'foc-tracker-verified-ux';

function loadVerifiedMap(): Record<string, boolean> {
  try {
    const raw = window.localStorage.getItem(VERIFIED_UX_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') return {};
    return parsed as Record<string, boolean>;
  } catch {
    return {};
  }
}

/**
 * Hub dashboard: proportion of FOC Requirements TOC sections that have a product tour.
 */
export function FocRequirementsTrackerView() {
  const coverage = useMemo(() => computeFocTourCoverage(), []);
  const [search, setSearch] = useState('');
  const [verifiedById, setVerifiedById] = useState<Record<string, boolean>>(
    () => loadVerifiedMap()
  );

  const setVerified = useCallback((sectionId: string, verified: boolean) => {
    setVerifiedById((prev) => {
      const next = { ...prev, [sectionId]: verified };
      try {
        window.localStorage.setItem(
          VERIFIED_UX_STORAGE_KEY,
          JSON.stringify(next)
        );
      } catch {
        // ignore quota / private mode
      }
      return next;
    });
  }, []);

  const verifiedStats = useMemo(() => {
    const verified = coverage.rows.filter(
      (row) => verifiedById[row.id] === true
    ).length;
    const total = coverage.total;
    const percent = total === 0 ? 0 : Math.round((verified / total) * 100);
    return { verified, total, percent };
  }, [coverage.rows, coverage.total, verifiedById]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return coverage.rows;
    return coverage.rows.filter((row) => {
      if (row.id.toLowerCase().includes(q)) return true;
      if (row.title.toLowerCase().includes(q)) return true;
      const status = row.covered ? 'covered' : 'missing';
      if (status.includes(q)) return true;
      if (row.tourLabels.some((label) => label.toLowerCase().includes(q))) {
        return true;
      }
      const verifiedLabel = verifiedById[row.id] === true ? 'yes' : 'no';
      return verifiedLabel.includes(q);
    });
  }, [coverage.rows, search, verifiedById]);

  return (
    <div
      className="dark tw:flex tw:flex-col tw:gap-4 tw:p-4 tw:text-foreground"
      data-testid="foc-requirements-tracker"
    >
      <div>
        <h2 className="tw:text-lg tw:font-semibold">FOC Requirements Tracker</h2>
        <p className="tw:mt-1 tw:text-sm tw:text-muted-foreground">
          Product-tour coverage against FOC Requirements document sections
          (3.1–25.3).
        </p>
      </div>

      <div className="tw:grid tw:gap-3 tw:sm:grid-cols-2 tw:xl:grid-cols-5">
        <Card>
          <CardHeader className="tw:pb-2">
            <CardDescription>Requirements</CardDescription>
            <CardTitle className="tw:text-2xl tw:tabular-nums">
              {coverage.total}
            </CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="tw:pb-2">
            <CardDescription>With tour</CardDescription>
            <CardTitle className="tw:text-2xl tw:tabular-nums">
              {coverage.covered}
            </CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="tw:pb-2">
            <CardDescription>Missing tour</CardDescription>
            <CardTitle className="tw:text-2xl tw:tabular-nums">
              {coverage.missing}
            </CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="tw:pb-2">
            <CardDescription>Coverage</CardDescription>
            <CardTitle className="tw:text-2xl tw:tabular-nums">
              {coverage.percent}%
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Progress value={coverage.percent}>
              <ProgressLabel className="tw:sr-only">Tour coverage</ProgressLabel>
              <ProgressValue />
            </Progress>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="tw:pb-2">
            <CardDescription>Verified UX by USCG</CardDescription>
            <CardTitle className="tw:text-2xl tw:tabular-nums">
              {verifiedStats.percent}%
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Progress value={verifiedStats.percent}>
              <ProgressLabel className="tw:sr-only">
                Verified UX coverage
              </ProgressLabel>
              <ProgressValue />
            </Progress>
            <p className="tw:mt-2 tw:text-xs tw:text-muted-foreground">
              {verifiedStats.verified}/{verifiedStats.total} verified
            </p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="tw:gap-3">
          <div>
            <CardTitle className="tw:text-base">Requirements Breakdown</CardTitle>
            <CardDescription>
              Each FOC Requirements TOC section and its covering product tour(s).
            </CardDescription>
          </div>
          <div className="tw:relative tw:max-w-md">
            <Search className="tw:pointer-events-none tw:absolute tw:left-2.5 tw:top-1/2 tw:size-4 tw:-translate-y-1/2 tw:text-muted-foreground" />
            <Input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search requirements…"
              aria-label="Search requirements"
              className="tw:pl-8"
            />
          </div>
        </CardHeader>
        <CardContent className="tw:overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="tw:w-24">Section</TableHead>
                <TableHead>Title</TableHead>
                <TableHead className="tw:w-28">Status</TableHead>
                <TableHead>Product tour</TableHead>
                <TableHead className="tw:w-44">Verified UX by USCG</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredRows.map((row) => {
                const verified = verifiedById[row.id] === true;
                const checkboxId = `foc-verified-${row.id}`;
                return (
                  <TableRow key={row.id}>
                    <TableCell className="tw:font-medium tw:tabular-nums">
                      {row.id}
                    </TableCell>
                    <TableCell>{row.title}</TableCell>
                    <TableCell>
                      {row.covered ? (
                        <Badge variant="secondary">Covered</Badge>
                      ) : (
                        <Badge variant="outline">Missing</Badge>
                      )}
                    </TableCell>
                    <TableCell className="tw:text-sm">
                      {row.tours.length > 0 ? (
                        <div className="tw:flex tw:flex-col tw:gap-1">
                          {row.tours.map((tour) => (
                            <Link
                              key={tour.href}
                              to={tour.href}
                              className="tw:text-primary tw:underline-offset-4 tw:hover:underline"
                            >
                              {tour.label}
                            </Link>
                          ))}
                        </div>
                      ) : (
                        <span className="tw:text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="tw:flex tw:items-center tw:gap-2">
                        <Checkbox
                          id={checkboxId}
                          checked={verified}
                          onCheckedChange={(checked) =>
                            setVerified(row.id, checked === true)
                          }
                        />
                        <Label
                          htmlFor={checkboxId}
                          className="tw:text-sm tw:font-normal tw:text-muted-foreground"
                        >
                          {verified ? 'Yes' : 'No'}
                        </Label>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {filteredRows.length === 0 ? (
            <p className="tw:py-6 tw:text-center tw:text-sm tw:text-muted-foreground">
              No requirements match your search.
            </p>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
